import type {
  HistogramBucket,
  DistributionItem,
} from './DistributionSection.types'

/**
 * Process area histogram buckets into distribution items with grouped categories
 */
export const processAreaBuckets = (
  buckets: HistogramBucket[]
): DistributionItem[] => {
  // Older snapshots include the -25 histogram bucket for unknown area (-1).
  buckets = buckets.filter((bucket) => bucket.key >= 0)
  const total = buckets.reduce((sum, b) => sum + b.doc_count, 0)
  if (total === 0) return []

  // Define area categories with thresholds (in m²)
  const categories = [
    { label: '<50', max: 50 },
    { label: '50-75', min: 50, max: 75 },
    { label: '75-100', min: 75, max: 100 },
    { label: '100-125', min: 100, max: 125 },
    { label: '125-150', min: 125, max: 150 },
    { label: '150-200', min: 150, max: 200 },
    { label: '200-300', min: 200, max: 300 },
    { label: '>300', min: 300 },
  ]

  // Initialize counts for each category
  const groupedCounts: Record<string, number> = {}
  categories.forEach((cat) => {
    groupedCounts[cat.label] = 0
  })

  // Group buckets into categories
  buckets.forEach((bucket) => {
    const area = bucket.key
    for (const cat of categories) {
      const minVal = cat.min ?? 0
      const maxVal = cat.max ?? Infinity
      if (area >= minVal && area < maxVal) {
        groupedCounts[cat.label] += bucket.doc_count
        break
      }
    }
  })

  return categories
    .filter((cat) => groupedCounts[cat.label] > 0)
    .map((cat) => ({
      label: cat.label,
      value: cat.min ?? 0,
      count: groupedCounts[cat.label],
      percentage: (groupedCounts[cat.label] / total) * 100,
    }))
}
